import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../lib/useAsync";
import type {
  RosterAllowance,
  RosterEntryView,
  RosterLevel,
} from "../api/types";

const LEVEL_LABEL: Record<RosterLevel, string> = {
  youth: "Youth",
  ms: "Middle School",
  custom: "Custom",
};

interface LinkOption {
  wrestlerId: string;
  name: string;
}

/**
 * Team weight-class roster. Admins pick a level template (Youth / Middle
 * School), tune the growth allowance (+0/+1/+2 lbs, applied dynamically to
 * every class), edit the base classes, and manage per-class roster entries
 * (wrestler name + parent contact). Members see a read-only view.
 */
export function TeamRosterPanel({
  teamId,
  isAdmin,
  onInviteParent,
}: {
  teamId: string;
  isAdmin: boolean;
  onInviteParent?: (email: string) => void;
}) {
  const roster = useAsync(() => api.getTeamRoster(teamId), [teamId]);
  // App wrestlers on this team (id + name) — used to link roster entries to a
  // real wrestler record. Standings endpoint already returns the team's roster
  // of app wrestlers; reuse it as the link source.
  const teamWrestlers = useAsync(
    () => (isAdmin ? api.getTeamStandings(teamId) : Promise.resolve({ teamId, items: [] })),
    [teamId, isAdmin],
  );
  const [err, setErr] = useState<string>();
  const [busy, setBusy] = useState(false);

  const config = roster.data?.config ?? null;
  const entries = roster.data?.entries ?? [];
  const allowance = config?.allowance ?? 0;

  // Group entries by their BASE weight class (effective = base + allowance).
  const byClass = useMemo(() => {
    const map = new Map<number, RosterEntryView[]>();
    for (const e of entries) {
      const arr = map.get(e.weightClass) ?? [];
      arr.push(e);
      map.set(e.weightClass, arr);
    }
    return map;
  }, [entries]);

  const classes = config?.baseWeightClasses ?? [];

  const applyTemplate = async (level: "youth" | "ms") => {
    setErr(undefined);
    setBusy(true);
    try {
      await api.saveRosterConfig(teamId, { level, allowance });
      await roster.reload();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const setAllowance = async (a: RosterAllowance) => {
    if (!config) return;
    setErr(undefined);
    setBusy(true);
    try {
      await api.saveRosterConfig(teamId, {
        level: config.level,
        baseWeightClasses: config.baseWeightClasses,
        allowance: a,
      });
      await roster.reload();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (roster.loading) return <div className="card">Loading roster…</div>;

  return (
    <div className="card">
      <div className="row spread">
        <h2 style={{ margin: 0 }}>Weight-class roster</h2>
        {config && (
          <span className="badge accent">
            {LEVEL_LABEL[config.level]}
            {allowance > 0 ? ` · +${allowance} lb` : ""}
          </span>
        )}
      </div>

      {!config && (
        <div style={{ marginTop: "0.5rem" }}>
          <p className="muted">
            {isAdmin
              ? "No roster set up yet. Start from a template:"
              : "The team admin hasn't set up a weight-class roster yet."}
          </p>
          {isAdmin && (
            <div className="row">
              <button disabled={busy} onClick={() => applyTemplate("youth")}>
                Use Youth template
              </button>
              <button
                className="secondary"
                disabled={busy}
                onClick={() => applyTemplate("ms")}
              >
                Use Middle School template
              </button>
            </div>
          )}
        </div>
      )}

      {config && (
        <>
          {isAdmin && (
            <div
              className="row"
              style={{ marginTop: "0.6rem", flexWrap: "wrap", gap: "0.75rem" }}
            >
              <label className="row" style={{ gap: "0.4rem" }}>
                <span className="muted">Growth allowance:</span>
                <div className="seg" role="group" aria-label="Growth allowance">
                  {([0, 1, 2] as RosterAllowance[]).map((a) => (
                    <button
                      key={a}
                      className={allowance === a ? "seg-on" : ""}
                      disabled={busy}
                      onClick={() => setAllowance(a)}
                    >
                      +{a}
                    </button>
                  ))}
                </div>
              </label>
              <label className="row" style={{ gap: "0.4rem" }}>
                <span className="muted">Template:</span>
                <button
                  className="secondary"
                  disabled={busy}
                  onClick={() => applyTemplate("youth")}
                >
                  Youth
                </button>
                <button
                  className="secondary"
                  disabled={busy}
                  onClick={() => applyTemplate("ms")}
                >
                  Middle School
                </button>
              </label>
            </div>
          )}
          {allowance > 0 && (
            <p className="muted" style={{ fontSize: "0.85rem" }}>
              Showing each class with the +{allowance} lb growth allowance
              applied.
            </p>
          )}

          <div className="roster" style={{ marginTop: "0.75rem" }}>
            {classes.map((base) => (
              <RosterClassRow
                key={base}
                teamId={teamId}
                base={base}
                effective={base + allowance}
                entries={byClass.get(base) ?? []}
                isAdmin={isAdmin}
                onChanged={() => roster.reload()}
                onInviteParent={onInviteParent}
                linkOptions={teamWrestlers.data?.items ?? []}
              />
            ))}
          </div>
        </>
      )}

      {err && <p className="error">{err}</p>}
    </div>
  );
}

function RosterClassRow({
  teamId,
  base,
  effective,
  entries,
  isAdmin,
  onChanged,
  onInviteParent,
  linkOptions,
}: {
  teamId: string;
  base: number;
  effective: number;
  entries: RosterEntryView[];
  isAdmin: boolean;
  onChanged: () => void;
  onInviteParent?: (email: string) => void;
  linkOptions: LinkOption[];
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [pName, setPName] = useState("");
  const [pEmail, setPEmail] = useState("");
  const [pPhone, setPPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.addRosterEntry(teamId, {
        weightClass: base,
        wrestlerName: name.trim(),
        parentName: pName.trim() || undefined,
        parentEmail: pEmail.trim() || undefined,
        parentPhone: pPhone.trim() || undefined,
      });
      setName("");
      setPName("");
      setPEmail("");
      setPPhone("");
      setAdding(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (entryId: string) => {
    if (!confirm("Remove this wrestler from the roster?")) return;
    await api.deleteRosterEntry(teamId, entryId);
    onChanged();
  };

  return (
    <div className="roster-class">
      <div className="roster-class-head">
        <span className="roster-weight">{effective}</span>
        <span className="muted"> lb{effective !== base ? ` (base ${base})` : ""}</span>
      </div>
      <div className="roster-entries">
        {entries.length === 0 && (
          <span className="muted roster-empty">— open —</span>
        )}
        {entries.map((e) => (
          <RosterEntryItem
            key={e.entryId}
            teamId={teamId}
            base={base}
            entry={e}
            isAdmin={isAdmin}
            onChanged={onChanged}
            onRemove={() => remove(e.entryId)}
            onInviteParent={onInviteParent}
            linkOptions={linkOptions}
          />
        ))}
      </div>

      {isAdmin &&
        (adding ? (
          <div className="roster-add">
            <input
              placeholder="Wrestler name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <input
              placeholder="Parent name (optional)"
              value={pName}
              onChange={(e) => setPName(e.target.value)}
            />
            <input
              placeholder="Parent email (optional)"
              value={pEmail}
              onChange={(e) => setPEmail(e.target.value)}
            />
            <input
              placeholder="Parent phone (optional)"
              value={pPhone}
              onChange={(e) => setPPhone(e.target.value)}
            />
            <div className="row">
              <button disabled={busy} onClick={add}>
                Save
              </button>
              <button
                className="secondary"
                disabled={busy}
                onClick={() => setAdding(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            className="secondary roster-add-btn"
            onClick={() => setAdding(true)}
          >
            + Add wrestler
          </button>
        ))}
    </div>
  );
}

function RosterEntryItem({
  teamId,
  base,
  entry,
  isAdmin,
  onChanged,
  onRemove,
  onInviteParent,
  linkOptions,
}: {
  teamId: string;
  base: number;
  entry: RosterEntryView;
  isAdmin: boolean;
  onChanged: () => void;
  onRemove: () => void;
  onInviteParent?: (email: string) => void;
  linkOptions: LinkOption[];
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(entry.wrestlerName);
  const [pName, setPName] = useState(entry.parentName ?? "");
  const [pEmail, setPEmail] = useState(entry.parentEmail ?? "");
  const [pPhone, setPPhone] = useState(entry.parentPhone ?? "");
  const [linkId, setLinkId] = useState(entry.wrestlerId ?? "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.updateRosterEntry(teamId, entry.entryId, {
        weightClass: base,
        wrestlerName: name.trim(),
        parentName: pName.trim(),
        parentEmail: pEmail.trim(),
        parentPhone: pPhone.trim(),
        wrestlerId: linkId ? linkId : null,
      });
      setEditing(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="roster-add">
        <input
          placeholder="Wrestler name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          placeholder="Parent name"
          value={pName}
          onChange={(e) => setPName(e.target.value)}
        />
        <input
          placeholder="Parent email"
          value={pEmail}
          onChange={(e) => setPEmail(e.target.value)}
        />
        <input
          placeholder="Parent phone"
          value={pPhone}
          onChange={(e) => setPPhone(e.target.value)}
        />
        <label className="muted" style={{ fontSize: "0.82rem" }}>
          Link to wrestler record (shows W–L + record):
          <select
            style={{ width: "100%", marginTop: "0.25rem" }}
            value={linkId}
            onChange={(e) => setLinkId(e.target.value)}
          >
            <option value="">— not linked —</option>
            {linkOptions.map((o) => (
              <option key={o.wrestlerId} value={o.wrestlerId}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
        <div className="row">
          <button disabled={busy} onClick={save}>
            Save
          </button>
          <button
            className="secondary"
            disabled={busy}
            onClick={() => setEditing(false)}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  const rec = entry.record;
  return (
    <div className="roster-entry">
      <div>
        <strong>{entry.wrestlerName}</strong>
        {rec && (rec.wins > 0 || rec.losses > 0) && (
          <span className="badge state" style={{ marginLeft: "0.4rem" }}>
            {rec.wins}–{rec.losses} · {rec.winPct}%
          </span>
        )}
        {entry.wrestlerId && (
          <Link
            to={`/record?w=${entry.wrestlerId}`}
            className="muted"
            style={{ marginLeft: "0.4rem", fontSize: "0.82rem" }}
          >
            View record →
          </Link>
        )}
        {(entry.parentName || entry.parentEmail || entry.parentPhone) && (
          <div className="muted" style={{ fontSize: "0.82rem" }}>
            {entry.parentName && <span>{entry.parentName}</span>}
            {entry.parentEmail && <span> · {entry.parentEmail}</span>}
            {entry.parentPhone && <span> · {entry.parentPhone}</span>}
          </div>
        )}
      </div>
      {isAdmin && (
        <div className="row" style={{ gap: "0.35rem" }}>
          <button className="secondary" onClick={() => setEditing(true)}>
            Edit
          </button>
          {entry.parentEmail && onInviteParent && (
            <button
              className="secondary"
              title="Invite this parent to the app"
              onClick={() => onInviteParent(entry.parentEmail!)}
            >
              Invite
            </button>
          )}
          <button className="secondary" onClick={onRemove}>
            Remove
          </button>
        </div>
      )}
    </div>
  );
}
